import { Endereco, Logradouro, EnderecoCreate } from './endereco';
import { Municipio } from './localidade';

export interface Empreendimento {
  id: number;
  imagens:string[];
  vagas:number;
  vagas_rotativas:number;
  tabela_preco: string;
  book?: string | null;
  arquivo_extra_1?: string | null;
  arquivo_extra_2?: string | null;
  endereco: Endereco;
  fase_empreendimento?: number; // writeOnly
  tipo_empreendimento?: string[]; // writeOnly
  item_empreendimento?: string[]; // writeOnly
  fase_atual?: number; // readOnly
  tipo_empreendimento_descricao?: string[]; // readOnly
  itens_empreendimento_descricoes?: string[]; // readOnly
  created_by: string;
  modified_by: string;
  created: string;
  modified: string;
  nome: string;
  area_total:number;
  area_construida:number;
  is_lancamento: boolean;
  data_entrega?: string;
  data_inicio_comercializacao?: string;
  observacao?: string;
  heading?: string;
  pitch?: string;
  incorporadora: number;
  elevadores: number;
}

export interface EmpreendimentoCreate {
  vagas_rotativas:number;
  vagas:number;
  endereco: number | EnderecoCreate;
  fase_empreendimento?: number;
  tabela_preco: string;
  book?: File;
  arquivo_extra_1?: File;
  arquivo_extra_2?: File;
  imagens?:File;
  tipo_empreendimento?: number[];
  item_empreendimento?: string[];
  area_total:number;
  area_construida:number;
  is_lancamento: boolean;
  nome: string;
  data_entrega?: string;
  data_inicio_comercializacao?: string;
  observacao?: string;
  heading?: string;
  pitch?: string;
  incorporadora: number;
  elevadores: number;
}

export interface EmpreendimentoUpdate {
  endereco?: number | EnderecoCreate;
  fase_empreendimento?: number;
  tipo_empreendimento?: number[];
  item_empreendimento?: string[];
  nome?: string;
  quantidade_garagens?: number;
  data_entrega?: string;
  data_inicio_comercializacao?: string;
  observacao?: string;
  heading?: string;
  pitch?: string;
  incorporadora?: number;
}

export interface PaginatedEmpreendimentoResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Empreendimento[];
}

// Novas interfaces para gerenciamento dinâmico
export interface TipoEmpreendimento {
  id: number;
  Nome_empreendimento: string;
  descricao_empreendimento?: string;
}


export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface ItemEmpreendimento {
  id: string;
  name: string;
  tipo_item_empreendimento: number;
  categoria: number;
  descricao?: string;
  created: string;
  modified: string;
}

export interface TipoEmpreendimentoCreate {
  nome: string;
  descricao: string;
}

export interface TipoParteEmpreendimento {
  id: string;
  Nome_tipo_parte_empreendimento: string;
  descricao?: string;
  atributos_definidos: Record<string, any>;
  created: string;
  modified: string;
}

export interface TipoParteEmpreendimentoCreate {
  Nome_tipo_parte_empreendimento: string;
  descricao?: string;
  atributos_definidos: Record<string, any>;
}

export interface ItemEmpreendimentoCreate {
  name: string;
  tipo_item_empreendimento: number;
  descricao?: string;
}

// Enums existentes
export enum FaseEmpreendimentoEnum {
  PLANTA = 1,
  OBRAS = 2,
  PRONTO = 3
}

// Única fonte de verdade pro rótulo/opções de fase — bate com
// empreendimento.models.FaseEmpreendimento no backend (só 3 valores).
// Antes existiam pelo menos 2 mapas divergentes no frontend (um em
// VisualizarEmpreendimento.tsx com 5 fases fantasmas — Planta/Fundação/
// Estrutura/Acabamento/Pronto — que nunca correspondiam ao que a API
// realmente envia, e outro em types/movimentacao.ts com a mesma invenção,
// não utilizado em lugar nenhum). Qualquer fase >= 4 caía no fallback
// "Fase 2"/"Fase 3" em vez de um rótulo de verdade.
export const FASE_EMPREENDIMENTO_LABELS: Record<FaseEmpreendimentoEnum, string> = {
  [FaseEmpreendimentoEnum.PLANTA]: 'Planta',
  [FaseEmpreendimentoEnum.OBRAS]: 'Obras',
  [FaseEmpreendimentoEnum.PRONTO]: 'Pronto',
};

export const FASE_EMPREENDIMENTO_OPTIONS = [
  { value: FaseEmpreendimentoEnum.PLANTA, label: FASE_EMPREENDIMENTO_LABELS[FaseEmpreendimentoEnum.PLANTA] },
  { value: FaseEmpreendimentoEnum.OBRAS, label: FASE_EMPREENDIMENTO_LABELS[FaseEmpreendimentoEnum.OBRAS] },
  { value: FaseEmpreendimentoEnum.PRONTO, label: FASE_EMPREENDIMENTO_LABELS[FaseEmpreendimentoEnum.PRONTO] },
] as const;

export const getFaseEmpreendimentoLabel = (fase: number | string | undefined | null): string => {
  if (fase === undefined || fase === null || fase === '') return '-';
  const numero = typeof fase === 'string' ? parseInt(fase, 10) : fase;
  return FASE_EMPREENDIMENTO_LABELS[numero as FaseEmpreendimentoEnum] ?? `Fase ${fase}`;
};

export enum AnoDisponibilidadeEnum {
  ANO_2024 = 2024,
  ANO_2025 = 2025,
  ANO_2026 = 2026,
  ANO_2027 = 2027,
  ANO_2028 = 2028,
  ANO_2029 = 2029,
  ANO_2030 = 2030
}

export enum MesDisponibilidadeEnum {
  JANEIRO = 1,
  FEVEREIRO = 2,
  MARCO = 3,
  ABRIL = 4,
  MAIO = 5,
  JUNHO = 6,
  JULHO = 7,
  AGOSTO = 8,
  SETEMBRO = 9,
  OUTUBRO = 10,
  NOVEMBRO = 11,
  DEZEMBRO = 12
}

export enum OrigemRecursoEnum {
  PROPRIO = 1,
  SFH = 2,
  CONDOMINIO = 3,
  COOPERATIVA = 4,
  PROPRIO_SFH = 5,
  OUTROS = 6,
  MCMV = 7
}

export enum TipoParteEmpreendimentoEnum {
  APARTAMENTO = 1,
  CASA = 2,
  LOJA = 3,
  ESCRITORIO = 4,
  GALPAO = 5
}

export interface ParteEmpreendimento {
  segmento: number;
  id: number;
  torre:number;
  torre_texto?: string;
  ano_disponibilidade: AnoDisponibilidadeEnum;
  mes_disponibilidade: MesDisponibilidadeEnum;
  origem_recurso: OrigemRecursoEnum;
  created: string;
  modified: string;
  nome: string;
  area_privativa_m2: string;
  valor_imovel: string;
  valor_avaliacao?: string;
  forma_pagamento: string;
  atributos_extras: Record<string, any>;
  empreendimento: number;
  tipo_parte_empreendimento: number;
  disponibilidade: number;
  quantidade_garagem: number;
  mcmv:boolean;
  tipo_produto: number;
  andar: number;
  pavimento_texto?: string;
  numero_apartamento: number
  vagas_rotativas:number;
  rotativa_fixa?: number;
  rotativa_aleatoria?: number;

}

export interface ParteEmpreendimentoCreate {
  segmento: number;
  torre:number;
  torre_texto?: string;
  ano_disponibilidade: number; // Mudado de AnoDisponibilidadeEnum para number
  mes_disponibilidade: number; // Mudado de MesDisponibilidadeEnum para number
  origem_recurso: number; // Mudado de OrigemRecursoEnum para number
  nome: string;
  area_privativa_m2: string;
  valor_imovel: string;
  valor_avaliacao?: string;
  forma_pagamento: string;
  atributos_extras: Record<string, any>;
  empreendimento: number;
  tipo_parte_empreendimento: number;
  disponibilidade: number;
  quantidade_garagem: number;
  mcmv:boolean;
  tipo_produto: number;
  andar: number;
  pavimento_texto?: string;
  numero_apartamento: number
  vagas_rotativas:number;
  rotativa_fixa?: number;
  rotativa_aleatoria?: number;

}



export type EmpreendimentoEditPayload = Omit<Partial<Empreendimento>, 'endereco' | 'partes_empreendimento'> & {
  endereco?: EnderecoCreate;
  partes_empreendimento?: ParteEmpreendimentoCreate[];
};

export interface SelectOption {
  id: number | string;
  nome: string;
}